import {
  DownloadOutlined,
  Person,
  ThumbDownAlt,
  ThumbDownOffAlt,
  ThumbUpAlt,
  ThumbUpOffAlt,
  VolumeUp,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { createRef, useState } from "react";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";

import { IConversation } from "../types/chatbot";
import Graphs from "./graphs";
import StarIcon from "./star_icon";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import useIdleLogout from "../idleLogout";
import useAuthRedirect from "../userAutoRedirect";
import StopIcon from "@mui/icons-material/Stop";
import { QueryFeedback } from "../Controller/request";

const checkSession = () => {
  return !!sessionStorage.getItem("userDetails");
};

export default function Conversation({
  conversation,
  like,
  dislike,
}: {
  conversation: IConversation;
  like: () => void;
  dislike: () => void;
}) {
  const ref = createRef();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [lastId, setLastId] = useState<string | null>(null);
  const [lastStatus, setLastStatus] = useState<string | null>(null);

  function speak(message: string) {
    // Clean up the message: removing *,#
    const cleanedMessage = message.replace(/[#*]+/g, "");

    if (window.speechSynthesis.speaking) {
      setIsSpeaking(false);
      window.speechSynthesis.cancel();
    } else {
      setIsSpeaking(true);

      const speech = new SpeechSynthesisUtterance();
      const maxChunkLength = 150; // Maximum number of characters per chunk
      let messageChunks = splitMessageIntoChunks(
        cleanedMessage,
        maxChunkLength
      );

      let currentChunkIndex = 0;

      speech.lang = "en-US";
      speech.rate = 1;
      speech.pitch = 1;

      const setVoiceAndSpeak = () => {
        const voices = window.speechSynthesis.getVoices();
        const femaleVoice =
          voices.find((voice) => voice.name.includes("Female")) ||
          voices.find((voice) => voice.name === "Google US English Female") ||
          voices.find((voice) => voice.lang.startsWith("en"));
        speech.voice = femaleVoice || null;

        const speakNextChunk = () => {
          if (currentChunkIndex < messageChunks.length) {
            speech.text = messageChunks[currentChunkIndex++];
            window.speechSynthesis.speak(speech);
          } else {
            setIsSpeaking(false); // All chunks spoken
          }
        };

        // Event listener to speak the next chunk when the current one finishes
        speech.onend = speakNextChunk;

        // Start with the first chunk
        speakNextChunk();
      };

      if (window.speechSynthesis.getVoices().length === 0) {
        window.speechSynthesis.onvoiceschanged = setVoiceAndSpeak;
      } else {
        setVoiceAndSpeak();
      }
    }
  }

  // Helper function to split the message into chunks without cutting words in half
  function splitMessageIntoChunks(
    message: string,
    maxChunkLength: number
  ): string[] {
    const words = message.split(" ");
    let chunks: string[] = [];
    let currentChunk = "";

    words.forEach((word) => {
      if ((currentChunk + word).length <= maxChunkLength) {
        currentChunk += (currentChunk ? " " : "") + word;
      } else {
        chunks.push(currentChunk);
        currentChunk = word; // Start a new chunk with the current word
      }
    });

    // Add the last chunk
    if (currentChunk) {
      chunks.push(currentChunk);
    }

    return chunks;
  }

  function download() {
    try {
      (window as any)
        .html2pdf()
        .set({ filename: `conversation.pdf` })
        .from(ref.current)
        .save();
    } catch (e) {
      console.error(e);
    }
  }

  useIdleLogout();
  const sessionExists = checkSession();
  useAuthRedirect(sessionExists);
  if (!sessionExists) {
    return null;
  }

  const userDetailsString = sessionStorage.getItem("userDetails");
  const userDetailsJson = userDetailsString
    ? JSON.parse(userDetailsString)
    : null;
  const userDetailsObject = userDetailsJson ? userDetailsJson : {};

  function getInitials(name: string): string {
    const words = name.trim().split(" ");

    let initials = "";

    if (words.length === 2) {
      initials = words[0].charAt(0) + words[1].charAt(0);
    } else if (words.length >= 3) {
      initials = words[0].charAt(0) + words[2].charAt(0);
      // initials = words[0].charAt(0) + words[words.length - 1].charAt(0);
    } else if (words.length === 1) {
      initials = words[0].charAt(0);
    }

    return initials.toUpperCase();
  }
  const name = userDetailsObject["Name"];
  const initials = getInitials(name);

  const handleClick = async (id: any, status: string) => {
    if (lastId === id && lastStatus === status) {
      return;
    }

    setLastId(id);
    setLastStatus(status);

    try {
      await QueryFeedback({ chatId: id, thumpup: status });
    } catch (error) {
      console.error(error);
    }
  };

  const handleDownload = async (fileUrl: string) => {
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error("Failed to download the file");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileUrl.split("/").pop() || "downloaded-file";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const documentUrls: string[] = conversation.resultDocument ?? [];

  // const documentLinks = documentUrls.length > 0
  //   ? documentUrls.map((url, index) => {
  //     const fileNameWithExtension = decodeURIComponent(url.substring(url.lastIndexOf('/') + 1));
  //     const fileName = fileNameWithExtension.substring(0, fileNameWithExtension.lastIndexOf('.')) || fileNameWithExtension;
  //     const directoryPath = url.substring(url.indexOf('.com/') + 5, url.lastIndexOf('/'));

  //     return (
  //       <div key={index}>
  //         <a
  //           href={url}
  //           onClick={(e) => {
  //             e.preventDefault();
  //             handleDownload(url);
  //           }}
  //           style={{ color: '#3c87cd', fontSize: '13px' }}
  //         >
  //           {`${directoryPath}/${fileName}`}
  //         </a>
  //       </div>
  //     );
  //   })
  //   : null;

  const documentLinks =
    documentUrls.length > 0 ? (
      <>
        <div
          style={{
            border: "1px solid #8c8484",
            borderRadius: "15px",
            backgroundColor: "#ecedf2",
            padding: "3px 11px 3px 11px",
            marginBottom: "0px",
            textAlign: "center",
            fontSize: "13px",
            color: "#333",
            maxWidth: "fit-content",
            display: "inline-block",
            fontWeight: 600,
          }}
        >
          For more information, please refer to the below link:
        </div>
        {/* {documentUrls.map((url, index) => {
          const fileNameWithExtension = decodeURIComponent(url.substring(url.lastIndexOf('/') + 1));
          const fileName = fileNameWithExtension.substring(0, fileNameWithExtension.lastIndexOf('.')) || fileNameWithExtension;
          const directoryPath = url.substring(url.indexOf('.com/') + 5, url.lastIndexOf('/'));

          return (
            <div key={index}>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#3c87cd', fontSize: '13px', paddingLeft: '5px', textDecoration: 'none' }}
              >
                {`${fileName}`}
              </a>
            </div>
          );
        })} */}
        <ul style={{ paddingLeft: 25, listStyleType: "disc", margin: 2 }}>
          {documentUrls.map((url, index) => {
            const fileNameWithExtension = decodeURIComponent(
              url.substring(url.lastIndexOf("/") + 1)
            );
            const fileName =
              fileNameWithExtension.substring(
                0,
                fileNameWithExtension.lastIndexOf(".")
              ) || fileNameWithExtension;

            return (
              <li key={index} style={{ marginBottom: "1px" }}>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#3c87cd",
                    fontSize: "13px",
                    textDecoration: "none",
                    paddingLeft: "5px",
                  }}
                >
                  {`${fileName}`}
                </a>
              </li>
            );
          })}
        </ul>
      </>
    ) : null;

  return (
    <Box mb={isSmallScreen ? 2 : 4} ref={ref}>
      <Box px={isSmallScreen ? 0 : 2}>
        <List disablePadding>
          <ListItem sx={{ alignItems: "flex-start" }}>
            <ListItemAvatar>
              <Avatar
                sx={{
                  bgcolor: "#1A73C6",
                  width: 35,
                  height: 35,
                }}
              >
                {initials}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primaryTypographyProps={{ sx: { fontSize: "15px" } }}
              primary={
                <span
                  dangerouslySetInnerHTML={{
                    __html: conversation.query.replace(/\n/g, "<br/>"),
                  }}
                />
              }
            />
          </ListItem>
        </List>
      </Box>

      <Box
        ml={isSmallScreen ? 1 : 2}
        display={"flex"}
        alignItems={"flex-start"}
        pt={isSmallScreen ? 0 : 2}
      >
        <Box>
          <StarIcon isLoading={conversation.isLoading} />
        </Box>
        <Box flex={1} pr={1} pl={isSmallScreen ? 0 : 1}>
          {conversation.isLoading ? (
            <Box pl={1}>
              <Skeleton height={40} width={280} />
            </Box>
          ) : (
            <div
            // dangerouslySetInnerHTML={{
            //   __html: (conversation.result || "").replace(/\n/ig,"<br />"),
            // }}
            >
              {/* <div style={{ fontSize: '15px', hyphens: 'auto', wordBreak: 'break-word', textAlign: 'justify' }}> */}
              <div
                style={{
                  fontSize: "15px",
                  paddingRight: isSmallScreen ? "0.5rem" : "0.2rem",
                }}
              >
                <Markdown
                  rehypePlugins={[rehypeRaw, rehypeSanitize]}
                  components={{
                    a: ({ node, ...props }) => (
                      <a {...props} target="_blank" rel="noopener noreferrer">
                        {props.children}
                      </a>
                    ),
                  }}
                >
                  {(
                    conversation.result ||
                    "Something went wrong. Please try again!!!"
                  ).replace(/\|.*\|/gi, "")}
                </Markdown>
                <Box mt={2}>{documentLinks}</Box>
              </div>
            </div>
          )}

          {!conversation.isLoading && conversation.isPlottable ? (
            <Graphs
              type={conversation.displyType}
              data={conversation.message}
            />
          ) : null}

          {!conversation.isLoading ? (
            <Box pt={1} display={"flex"}>
              <IconButton onClick={() => speak(conversation.result!)}>
                {!isSpeaking ? (
                  <VolumeUp sx={{ color: "#1A73C6" }} />
                ) : (
                  <StopIcon sx={{ color: "#1A73C6" }} />
                )}
              </IconButton>
              <IconButton
                onClick={() => {
                  like();
                  handleClick(conversation.resultId, "yes");
                }}
              >
                {conversation.isLiked === true ? (
                  <ThumbUpAlt sx={{ color: "#1A73C6" }} />
                ) : (
                  <ThumbUpOffAlt sx={{ color: "#1A73C6" }} />
                )}
              </IconButton>
              <IconButton
                onClick={() => {
                  dislike();
                  handleClick(conversation.resultId, "no");
                }}
              >
                {conversation.isLiked === false ? (
                  <ThumbDownAlt sx={{ color: "#1A73C6" }} />
                ) : (
                  <ThumbDownOffAlt sx={{ color: "#1A73C6" }} />
                )}
              </IconButton>
              {/* <IconButton onClick={download}>
                <AutorenewIcon sx={{ color: "#1A73C6" }} />
              </IconButton> */}
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
